import { useState } from 'react'
import { useDelete, useNotify, useRedirect, useRefresh } from 'ra-core'
import { Confirm } from '@internal/ui-kit/components/admin/confirm'
import { Button } from '@internal/ui-kit/components/ui/button'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'

/**
 * `updateEmail` (`retool-exports/users/queries.json`, `PATCH
 * v2/admin/users/{id}/email`) — MIGRATION-NOTES.md's own "Deferred" section
 * flagged this as "a candidate for its own small, bespoke action… same shape
 * `apps/orders`' `OrderRefundButton` uses". Ticket feedback now asks for
 * exactly that, moved to a new "Danger zone" tab. The source's own
 * confirmation copy ("resends all order receipts and all notifications
 * about confirmation of bookings") is preserved verbatim in the confirm
 * dialog, not dropped.
 */
function ChangeEmailSection({ userId, currentEmail }: { userId: string; currentEmail: string | null | undefined }) {
  const [email, setEmail] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()

  const changeEmail = async () => {
    setIsSubmitting(true)
    try {
      await httpClient(`/v2/admin/users/${userId}/email`, {
        method: 'PATCH',
        body: JSON.stringify({ email, old_email: currentEmail, user_id: userId }),
      })
      notify('Email changed', { type: 'success' })
      setConfirmOpen(false)
      setEmail('')
      refresh()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not change this email.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="flex flex-col gap-3">
      <h4 className="font-medium">Change email</h4>
      <div className="flex flex-col gap-1.5 max-w-sm">
        <Label htmlFor="new-email">New email</Label>
        <Input id="new-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
      </div>
      <div>
        <Button type="button" variant="destructive" disabled={!email} onClick={() => setConfirmOpen(true)}>
          Change email
        </Button>
      </div>
      <Confirm
        isOpen={confirmOpen}
        loading={isSubmitting}
        title="Change this user's email?"
        content="This resends all order receipts and all notifications about confirmation of bookings."
        onConfirm={changeEmail}
        onClose={() => setConfirmOpen(false)}
      />
    </section>
  )
}

/** `removeUser` (`retool-exports/users/queries.json`, `DELETE v2/admin/users/{id}`) — the same real `deletePath` `apps/users`' own `DeleteButton` would use, via `useDelete` (not a raw `httpClient` call) so it stays in sync with `dataProvider.ts`'s confirmed `users` config. */
function RemoveUserSection({ userId }: { userId: string }) {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const notify = useNotify()
  const redirect = useRedirect()
  const [deleteOne, { isPending }] = useDelete()

  const removeUser = () => {
    deleteOne(
      'users',
      { id: userId },
      {
        onSuccess: () => {
          notify('User removed', { type: 'success' })
          setConfirmOpen(false)
          redirect('list', 'users')
        },
        onError: (err) => {
          notify(extractErrorMessage(err, 'Could not remove this user.'), { type: 'error' })
        },
      },
    )
  }

  return (
    <section className="flex flex-col gap-3">
      <h4 className="font-medium">Remove user</h4>
      <p className="text-muted-foreground text-sm">This permanently removes the user account. This cannot be undone.</p>
      <div>
        <Button type="button" variant="destructive" onClick={() => setConfirmOpen(true)}>
          Remove user
        </Button>
      </div>
      <Confirm
        isOpen={confirmOpen}
        loading={isPending}
        title="Remove this user?"
        content="This permanently removes the user account. This cannot be undone."
        onConfirm={removeUser}
        onClose={() => setConfirmOpen(false)}
      />
    </section>
  )
}

/**
 * The combined page's "Danger zone" tab (HATCH-101 round 4 — ticket
 * feedback: "danger zone" tab with change-email and user-removal actions,
 * both previously deferred as out-of-scope bespoke actions, see
 * MIGRATION-NOTES.md.
 */
export function DangerZoneTab({ userId, email }: { userId: string; email: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-8">
      <ChangeEmailSection userId={userId} currentEmail={email} />
      <RemoveUserSection userId={userId} />
    </div>
  )
}
